import React from "react"
import { ExtractionResult } from "./Usesaisiedeclarationst"

interface Props {
     factureFile: File
     extracted: ExtractionResult
     fraisFiles: File[]
     isSaving: boolean
     onUpdateField: (field: keyof ExtractionResult, value: number) => void
     onAddFraisFiles: (files: FileList | null) => void
     onRemoveFraisFile: (index: number) => void
     onSubmit: () => void
     onReset: () => void
}

const StepReviewForm = ({ factureFile, extracted, fraisFiles, isSaving, onUpdateField, onAddFraisFiles, onRemoveFraisFile, onSubmit, onReset }: Props) => {
     const fraisInputRef = React.useRef<HTMLInputElement>(null)
     const fraisRequired = extracted.fraisHt > 0
     const prestationValid = extracted.prestationHt > 0 && extracted.prestationVat > 0 && extracted.nbJoursTravailles > 0
     const canSubmit = prestationValid && (!fraisRequired || fraisFiles.length > 0)

     const totalHt = extracted.prestationHt + extracted.fraisHt
     const totalVat = extracted.prestationVat + extracted.fraisVat

     const handlePreviewFacture = () => {
          const url = URL.createObjectURL(factureFile)
          window.open(url, "_blank")
     }

     return (
          <div className="d-flex flex-column gap-6">
               {/* ── Facture de prestation ── */}
               <div className="d-flex align-items-center gap-3 p-4 bg-light-success rounded-3 border border-success border-dashed">
                    <i className="fas fa-file-check text-success fs-2" />
                    <div className="flex-grow-1 min-w-0">
                         <span className="fw-bold text-gray-800 fs-7 d-block">Facture de prestation</span>
                         <span className="text-gray-600 fs-8 text-truncate d-block">{factureFile.name}</span>
                    </div>
                    <button type="button" className="btn btn-sm btn-light-primary px-3" onClick={handlePreviewFacture} title="Voir la facture">
                         <i className="fas fa-eye fs-7" />
                    </button>
                    <button type="button" className="btn btn-sm btn-light-danger px-3" onClick={onReset} title="Changer de fichier">
                         <i className="fas fa-redo fs-7" />
                    </button>
               </div>

               {/* ── Prestation ── */}
               <div>
                    <h6 className="fw-bold text-gray-700 mb-4">
                         <i className="fas fa-file-invoice-dollar text-primary me-2" />
                         Prestation
                    </h6>
                    <div className="row g-4">
                         <NumberField label="Montant HT (€)" value={extracted.prestationHt} onChange={v => onUpdateField("prestationHt", v)} required placeholder="Montant HT" />
                         <NumberField label="TVA (€)" value={extracted.prestationVat} onChange={v => onUpdateField("prestationVat", v)} required placeholder="TVA" />
                         <NumberField label="Jours travaillés" value={extracted.nbJoursTravailles} onChange={v => onUpdateField("nbJoursTravailles", v)} step={0.5} required placeholder="Nb jours" />
                    </div>
               </div>

               {/* ── Frais ── */}
               <div>
                    <h6 className="fw-bold text-gray-700 mb-4">
                         <i className="fas fa-receipt text-warning me-2" />
                         Frais
                    </h6>
                    <div className="row g-4">
                         <NumberField label="Montant HT (€)" value={extracted.fraisHt} onChange={v => onUpdateField("fraisHt", v)} highlight={extracted.fraisHt > 0} />
                         <NumberField label="TVA (€)" value={extracted.fraisVat} onChange={v => onUpdateField("fraisVat", v)} highlight={extracted.fraisHt > 0} />
                    </div>

                    {/* Facture(s) de frais — mandatory when fraisHt > 0 */}
                    {fraisRequired && (
                         <div className="mt-4">
                              <div className="d-flex align-items-center justify-content-between mb-3">
                                   <span className="fw-semibold text-gray-700 fs-7">
                                        Facture(s) de frais <span className="text-danger">*</span>
                                   </span>
                                   <button type="button" className="btn btn-sm btn-light-warning" onClick={() => fraisInputRef.current?.click()}>
                                        <i className="fas fa-plus me-1" /> Ajouter
                                   </button>
                                   <input ref={fraisInputRef} type="file" multiple accept="application/pdf,image/*" className="d-none" onChange={e => onAddFraisFiles(e.target.files)} />
                              </div>

                              {fraisFiles.length === 0 && (
                                   <div className="alert alert-warning py-3 fs-7 mb-0">
                                        <i className="fas fa-exclamation-triangle me-2" />
                                        Une facture de frais est <strong>obligatoire</strong> car le montant des frais HT est supérieur à 0.
                                   </div>
                              )}

                              {fraisFiles.length > 0 && (
                                   <div className="d-flex flex-column gap-2">
                                        {fraisFiles.map((f, i) => (
                                             <div key={i} className="d-flex align-items-center gap-3 p-3 bg-light rounded-2 border border-gray-200">
                                                  <i className="fas fa-file-invoice text-warning fs-6" />
                                                  <span className="flex-grow-1 fs-7 text-gray-700 text-truncate">{f.name}</span>
                                                  <button
                                                       type="button"
                                                       className="btn btn-icon btn-sm btn-light-primary"
                                                       onClick={() => {
                                                            const url = URL.createObjectURL(f)
                                                            window.open(url, "_blank")
                                                       }}
                                                       title="Voir"
                                                  >
                                                       <i className="fas fa-eye fs-8" />
                                                  </button>
                                                  <button type="button" className="btn btn-icon btn-sm btn-light-danger" onClick={() => onRemoveFraisFile(i)}>
                                                       <i className="fas fa-times fs-8" />
                                                  </button>
                                             </div>
                                        ))}
                                   </div>
                              )}
                         </div>
                    )}
               </div>

               {/* ── Totals summary ── */}
               <div className="d-flex gap-4 p-4 bg-light-primary rounded-3 border border-primary border-dashed">
                    <div className="flex-grow-1">
                         <span className="text-gray-500 fs-8 d-block">Total HT</span>
                         <span className="fw-bold text-primary fs-5">{totalHt.toFixed(2)} €</span>
                    </div>
                    <div className="flex-grow-1">
                         <span className="text-gray-500 fs-8 d-block">Total TVA</span>
                         <span className="fw-bold text-primary fs-5">{totalVat.toFixed(2)} €</span>
                    </div>
                    <div className="flex-grow-1">
                         <span className="text-gray-500 fs-8 d-block">Total TTC</span>
                         <span className="fw-bold text-primary fs-5">{(totalHt + totalVat).toFixed(2)} €</span>
                    </div>
               </div>

               {/* ── Submit ── */}
               <div className="d-flex justify-content-end">
                    <button type="button" className="btn btn-primary fw-bold px-8" onClick={onSubmit} disabled={isSaving || !canSubmit}>
                         {isSaving ? (
                              <>
                                   <span className="spinner-border spinner-border-sm me-2" />
                                   Envoi en cours...
                              </>
                         ) : (
                              <>
                                   <i className="fas fa-paper-plane me-2" />
                                   Envoyer la déclaration
                              </>
                         )}
                    </button>
               </div>
          </div>
     )
}

// ─── Internal helper ──────────────────────────────────────────────────────────

interface NumberFieldProps {
     label: string
     value: number
     onChange: (v: number) => void
     step?: number
     highlight?: boolean
     required?: boolean
     placeholder?: string
}

const NumberField = ({ label, value, onChange, step = 0.01, highlight = false, required = false, placeholder }: NumberFieldProps) => {
     const showEmpty = required && value === 0
     const [localValue, setLocalValue] = React.useState<string>(showEmpty ? "" : value.toString())
     const invalid = required && value <= 0

     React.useEffect(() => {
          setLocalValue(required && value === 0 ? "" : value.toString())
     }, [value, required])

     return (
          <div className="col-6 col-md-4">
               <label className={`form-label fw-semibold fs-8 mb-1 ${highlight ? "text-warning" : "text-gray-600"}`}>
                    {label} {required && <span className="text-danger">*</span>}
               </label>
               <input
                    type="number"
                    className={`form-control form-control-sm ${highlight ? "border-warning" : ""} ${invalid ? "border-danger" : ""}`}
                    value={localValue}
                    step={step}
                    min={0}
                    placeholder={placeholder}
                    onChange={e => {
                         setLocalValue(e.target.value)
                         onChange(parseFloat(e.target.value) || 0)
                    }}
                    onBlur={() => {
                         if (!required && localValue === "") setLocalValue("0")
                    }}
               />
          </div>
     )
}

export default StepReviewForm
